import type { ReactNode } from 'react';
import { BlockIdProvider, useRender } from '../context';
import { getBlockDefinition } from '../registry';
import type { BlockNode, ColumnNode, DeviceFlags, PageContent, SectionNode } from '../types';

export function visibilityClasses(v?: DeviceFlags) {
  if (!v) return '';
  const c: string[] = [];
  if (!v.desktop) c.push('hidden-md', 'hidden-lg');
  if (!v.tablet) c.push('hidden-sm');
  if (!v.mobile) c.push('hidden-xs');
  return c.join(' ');
}

export function columnClasses(col: ColumnNode) {
  return `col-md-${col.width.desktop} col-sm-${col.width.tablet} col-xs-${col.width.mobile}`;
}

export interface RendererHooks {
  /** Editor canvas: wraps sections/blocks with selection and drop-zone UI. */
  wrapSection?: (section: SectionNode, index: number, node: ReactNode) => ReactNode;
  wrapBlock?: (block: BlockNode, section: SectionNode, column: ColumnNode, index: number, node: ReactNode) => ReactNode;
  wrapColumn?: (column: ColumnNode, section: SectionNode, node: ReactNode) => ReactNode;
  /** Editor shows inactive sections/blocks (dimmed); public rendering skips them. */
  includeInactive?: boolean;
}

export function BlockView({ block }: { block: BlockNode }) {
  const def = getBlockDefinition(block.type, block.version);
  if (!def) return <div className="kfz-unknown-block">Unknown component {block.type} v{block.version}</div>;
  const Component = def.component;
  const vis = visibilityClasses(block.visibility);
  const node = (
    <BlockIdProvider value={block.id}>
      <Component {...block.props} />
    </BlockIdProvider>
  );
  return vis ? <div className={vis}>{node}</div> : node;
}

function SectionView({ section, hooks }: { section: SectionNode; hooks: RendererHooks }) {
  const renderColumn = (col: ColumnNode) => {
    const blocks = col.blocks
      .filter((b) => hooks.includeInactive || b.isActive)
      .map((b, i) => {
        const view = <BlockView key={b.id} block={b} />;
        return hooks.wrapBlock ? <div key={b.id}>{hooks.wrapBlock(b, section, col, i, view)}</div> : view;
      });
    return hooks.wrapColumn ? hooks.wrapColumn(col, section, blocks) : blocks;
  };

  const vis = visibilityClasses(section.visibility);
  const single = section.columns.length === 1 && section.columns[0].width.desktop === 12;

  if (section.layout === 'full') {
    if (single) {
      return <div className={`kfz-section ${vis}`.trim()} data-section={section.id}>{renderColumn(section.columns[0])}</div>;
    }
    return (
      <div className={`kfz-section ${vis}`.trim()} data-section={section.id}>
        <div className="row kfz-row-flush">
          {section.columns.map((col) => (
            <div key={col.id} className={columnClasses(col)}>
              {renderColumn(col)}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`kfz-section ${section.preset || 'kfz-container-section'} ${vis}`.trim()} data-section={section.id}>
      <div className="container">
        <div className="row clearfix">
          {section.columns.map((col) => (
            <div key={col.id} className={columnClasses(col)}>
              {renderColumn(col)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function PageRenderer({ content, hooks = {} }: { content: PageContent; hooks?: RendererHooks }) {
  useRender();
  const sections = (content?.sections ?? []).filter((s) => hooks.includeInactive || s.isActive);
  return (
    <>
      {sections.map((s, i) => {
        const node = <SectionView key={s.id} section={s} hooks={hooks} />;
        return hooks.wrapSection ? <div key={s.id}>{hooks.wrapSection(s, i, node)}</div> : node;
      })}
    </>
  );
}
